import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Booking, type BookingActivity, type BookingLineItem, type BookingSettings, type BookingStatus, type Client, type Package } from "../lib/api";
import { InquirySlideOver } from "../components/InquirySlideOver";
import { LineItemsModal } from "../components/bookings/LineItemsModal";
import { ShootDoneModal } from "../components/bookings/ShootDoneModal";
import { EditModal } from "../components/bookings/EditModal";
import { formatPrice, formatDate } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import { STATUS_COLORS, useStatusLabels } from "../lib/status";

const COLUMN_ORDER: BookingStatus[] = ["inquiry", "booked", "processing", "completed", "cancelled"];
const COLUMN_LIMIT = 10;
const STALE_DAYS: Record<string, number> = { inquiry: 7, booked: 14, processing: 7 };
const MOBILE_LIMIT = 10;

export function BookingsPage() {
  const { t } = useTranslation();
  const STATUS_LABELS = useStatusLabels();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editBooking, setEditBooking] = useState<Booking | null>(null);
  const [shootDoneBooking, setShootDoneBooking] = useState<Booking | null>(null);
  const [lineItemsBooking, setLineItemsBooking] = useState<Booking | null>(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  // Expanded booking IDs + lazy-loaded line items per booking
  const [expandedID, setExpandedID] = useState<string | null>(null);
  const [lineItemsCache, setLineItemsCache] = useState<Record<string, BookingLineItem[]>>({});
  const [lineItemsLoading, setLineItemsLoading] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [attentionExpanded, setAttentionExpanded] = useState(true);
  const [activities, setActivities] = useState<Record<string, BookingActivity[]>>({});
  const [activityNote, setActivityNote] = useState<Record<string, string>>({});
  const [loadingActivities, setLoadingActivities] = useState<Record<string, boolean>>({});
  const [bookingSettings, setBookingSettings] = useState<BookingSettings>({ booking_archive_days: 90, nudge_stale_days: 7 });
  const [mobileShowAll, setMobileShowAll] = useState<Record<BookingStatus, boolean>>({} as Record<BookingStatus, boolean>);

  async function load(archive = false) {
    setLoading(true);
    setError(null);
    try {
      const [list, cls, pkgs, settings] = await Promise.all([
        api.listBookings(undefined, undefined, archive),
        api.listClients(),
        api.listPackages().catch(() => [] as Package[]),
        api.getBookingSettings().catch(() => ({ booking_archive_days: 90, nudge_stale_days: 7 } as BookingSettings)),
      ]);
      setBookings(list);
      setClients(cls);
      setPackages(pkgs);
      setBookingSettings(settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(showArchive); }, [showArchive]);

  async function handleExpandBooking(bookingID: string) {
    if (expandedID === bookingID) {
      setExpandedID(null);
      return;
    }
    setExpandedID(bookingID);
    // Load line items and activities in parallel on first expand
    const promises: Promise<unknown>[] = [];
    if (lineItemsCache[bookingID] === undefined) {
      setLineItemsLoading(true);
      promises.push(
        api.getBookingLineItems(bookingID)
          .then(items => setLineItemsCache(prev => ({ ...prev, [bookingID]: items })))
          .catch(() => {})
          .finally(() => setLineItemsLoading(false))
      );
    }
    promises.push(loadActivities(bookingID));
    await Promise.all(promises);
  }

  async function handleTransition(b: Booking, to: BookingStatus) {
    try {
      await api.transitionBooking(b.ID, to);
      await load(showArchive);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Transition failed");
    }
  }

  async function handleArchive(id: string) {
    try {
      await api.archiveBooking(id);
      await load(showArchive);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Archive failed");
    }
  }

  async function handleMarkPaid(id: string) {
    try {
      await api.updateBooking(id, { balance_paid: true });
      await load(showArchive);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    }
  }

  async function handleMarkDepositPaid(id: string) {
    try {
      await api.updateBooking(id, { deposit_paid: true });
      await load(showArchive);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    }
  }

  // Open LineItemsModal: load items into cache then open the modal.
  async function handleOpenLineItems(b: Booking) {
    let items = lineItemsCache[b.ID];
    if (items === undefined) {
      try {
        items = await api.getBookingLineItems(b.ID);
        setLineItemsCache(prev => ({ ...prev, [b.ID]: items }));
      } catch {
        items = [];
      }
    }
    setLineItemsBooking({ ...b, LineItems: items });
  }

  // Pre-load line items for a booking into cache (for ShootDoneModal summary).
  async function prefetchLineItems(bookingID: string) {
    if (lineItemsCache[bookingID] !== undefined) return;
    try {
      const items = await api.getBookingLineItems(bookingID);
      setLineItemsCache(prev => ({ ...prev, [bookingID]: items }));
    } catch {
      // silently ignore
    }
  }

  async function loadActivities(bookingID: string) {
    if (activities[bookingID] !== undefined) return;
    setLoadingActivities(prev => ({ ...prev, [bookingID]: true }));
    try {
      const acts = await api.listBookingActivities(bookingID);
      setActivities(prev => ({ ...prev, [bookingID]: acts }));
    } catch {
      setActivities(prev => ({ ...prev, [bookingID]: [] }));
    } finally {
      setLoadingActivities(prev => ({ ...prev, [bookingID]: false }));
    }
  }

  async function submitNote(bookingID: string) {
    const body = (activityNote[bookingID] ?? "").trim();
    if (!body) return;
    try {
      const act = await api.addBookingNote(bookingID, body);
      setActivities(prev => ({ ...prev, [bookingID]: [...(prev[bookingID] ?? []), act] }));
      setActivityNote(prev => ({ ...prev, [bookingID]: "" }));
    } catch {
      // ignore
    }
  }

  const grouped = COLUMN_ORDER.reduce<Record<BookingStatus, Booking[]>>((acc, s) => {
    acc[s] = bookings.filter(b => b.Status === s);
    return acc;
  }, {} as Record<BookingStatus, Booking[]>);

  // Stale: active bookings that haven't been updated in too long (and no upcoming shoot blocking it)
  const now = Date.now();
  const staleBookings = bookings.filter(b => {
    if (!["inquiry", "booked", "processing"].includes(b.Status)) return false;
    const threshold = (bookingSettings.nudge_stale_days || STALE_DAYS[b.Status] || 7) * 86400 * 1000;
    const stale = now - new Date(b.UpdatedAt).getTime() > threshold;
    if (!stale) return false;
    // Exception: booked with scheduled_at > 14 days away
    if (b.Status === "booked" && b.ScheduledAt) {
      const daysToShoot = (new Date(b.ScheduledAt).getTime() - now) / (86400 * 1000);
      if (daysToShoot > 14) return false;
    }
    return true;
  });

  return (
    <div style={{ padding: "1.5rem" }}>
      {editBooking && (
        <EditModal
          booking={editBooking}
          clients={clients}
          packages={packages}
          onClose={() => setEditBooking(null)}
          onSaved={async () => { setEditBooking(null); await load(showArchive); }}
        />
      )}
      {shootDoneBooking && (
        <ShootDoneModal
          booking={{ ...shootDoneBooking, LineItems: lineItemsCache[shootDoneBooking.ID] }}
          clients={clients}
          onClose={() => setShootDoneBooking(null)}
          onDone={async () => { setShootDoneBooking(null); await load(showArchive); }}
        />
      )}
      {lineItemsBooking && (
        <LineItemsModal
          booking={lineItemsBooking}
          packages={packages}
          onClose={() => setLineItemsBooking(null)}
          onSaved={(items) => {
            setLineItemsCache(prev => ({ ...prev, [lineItemsBooking.ID]: items }));
            setLineItemsBooking(null);
          }}
        />
      )}
      <InquirySlideOver
        open={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        onCreated={async () => { setInquiryOpen(false); await load(showArchive); }}
      />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <h1 style={{ margin: 0 }}>{t("bookings.title")}</h1>
        <button className="btn-primary" onClick={() => setInquiryOpen(true)}>
          {t("bookings.new_inquiry")}
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: "1rem" }}>{error}</div>}
      {loading && <p style={{ color: "var(--muted)" }}>{t("bookings.loading_items")}</p>}

      {!loading && staleBookings.length > 0 && (
        <div style={{ marginBottom: "1rem", border: "1px solid #f59e0b", borderRadius: 8, overflow: "hidden" }}>
          <button
            onClick={() => setAttentionExpanded(v => !v)}
            style={{ width: "100%", background: "color-mix(in srgb, #f59e0b 12%, transparent)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.6rem 0.85rem", color: "var(--text)", fontSize: "0.875rem", fontWeight: 600 }}
          >
            <span>⚠ {t("bookings.attention")}</span>
            <span style={{ background: "#f59e0b", color: "#fff", borderRadius: 999, fontSize: "0.75rem", padding: "0.05rem 0.45rem" }}>{staleBookings.length}</span>
            <span style={{ marginLeft: "auto", color: "var(--muted)", fontSize: "0.8rem" }}>{attentionExpanded ? `▲ ${t("bookings.collapse")}` : `▼ ${t("bookings.expand")}`}</span>
          </button>
          {attentionExpanded && (
            <div style={{ padding: "0.5rem 0.85rem 0.75rem", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {staleBookings.map(b => {
                const client = clients.find(c => c.ID === b.ClientID);
                const staleDays = Math.floor((now - new Date(b.UpdatedAt).getTime()) / (86400 * 1000));
                return (
                  <div key={b.ID} style={{ display: "flex", alignItems: "center", gap: "0.6rem", fontSize: "0.85rem", flexWrap: "wrap" }}>
                    <StatusBadge label={STATUS_LABELS[b.Status as BookingStatus]} color={STATUS_COLORS[b.Status as BookingStatus]} />
                    <strong>{b.Title}</strong>
                    {client && <span style={{ color: "var(--muted)" }}>{client.DisplayName}</span>}
                    <span style={{ color: "#f59e0b", marginLeft: "auto" }}>No activity for {staleDays}d</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!loading && (
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          <button
            className={!showArchive ? "btn-primary" : "btn-ghost"}
            style={{ fontSize: "0.825rem", padding: "0.3rem 0.7rem" }}
            onClick={() => setShowArchive(false)}
          >{t("bookings.active")}</button>
          <button
            className={showArchive ? "btn-primary" : "btn-ghost"}
            style={{ fontSize: "0.825rem", padding: "0.3rem 0.7rem" }}
            onClick={() => setShowArchive(true)}
          >{t("bookings.archive")}</button>
          {showArchive && <span style={{ color: "var(--muted)", fontSize: "0.8rem" }}>{t("bookings.archive_note", { days: bookingSettings.booking_archive_days })}</span>}
        </div>
      )}

      {!loading && (
        <div className="kanban-board" style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start", overflowX: "auto", paddingBottom: "1rem" }}>
          {COLUMN_ORDER.map(status => {
            const col = grouped[status];
            const visible = col.slice(0, COLUMN_LIMIT);
            const overflow = col.length - visible.length;
            const mobileAll = mobileShowAll[status] ?? false;
            const mobileVisible = mobileAll ? col : col.slice(0, MOBILE_LIMIT);
            const mobileHidden = col.length - mobileVisible.length;
            return (
              <div
                key={status}
                className="kanban-col"
                style={{ minWidth: 200, maxWidth: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: "0.5rem" }}
              >
                {/* Column header */}
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
                  <span style={{
                    background: STATUS_COLORS[status], color: "#fff",
                    padding: "0.2rem 0.6rem", borderRadius: 999,
                    fontSize: "0.75rem", fontWeight: 600,
                  }}>
                    {STATUS_LABELS[status]}
                  </span>
                  <span style={{ color: "var(--muted)", fontSize: "0.8rem" }}>{col.length}</span>
                </div>

                {col.length === 0 ? (
                  <div style={{ color: "var(--muted)", fontSize: "0.85rem", padding: "0.5rem 0.25rem" }}>{t("bookings.col_empty")}</div>
                ) : (
                  mobileVisible.map(b => {
                    const client = clients.find(c => c.ID === b.ClientID);
                    const cachedItems = lineItemsCache[b.ID];
                    const isExpanded = expandedID === b.ID;
                    return (
                      <div key={b.ID} className="card" style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                        <strong style={{ fontSize: "0.95rem", display: "block", marginBottom: "0.25rem" }}>{b.Title}</strong>
                        {client && <span style={{ color: "var(--muted)", fontSize: "0.8rem" }}>{client.DisplayName}</span>}
                        <div style={{ color: "var(--muted)", marginTop: "0.35rem", display: "flex", flexDirection: "column", gap: "0.15rem" }}>
                          {b.Location && <span>&#128205; {b.Location}</span>}
                          {b.ScheduledAt && <span>&#128197; {formatDate(b.ScheduledAt)}</span>}
                          {b.PriceCents > 0 && <span>&#128182; {formatPrice(b.PriceCents, b.Currency)}</span>}
                          {b.PackageID && (() => {
                            const pkg = packages.find(p => p.ID === b.PackageID);
                            return pkg ? <span>&#128230; {pkg.Name}</span> : null;
                          })()}
                          {b.DeliveryExpectedAt && <span>&#128666; by {formatDate(b.DeliveryExpectedAt)}</span>}
                        </div>
                        {/* Line items summary (from cache) */}
                        {cachedItems && cachedItems.length > 0 && (
                          <div style={{ fontSize: ".75rem", color: "var(--muted)", marginTop: ".25rem" }}>
                            {cachedItems.length} item{cachedItems.length !== 1 ? "s" : ""}
                            {" · "}
                            {formatPrice(cachedItems.reduce((s, i) => s + i.PriceCents * i.Quantity, 0), b.Currency || "EUR")}
                          </div>
                        )}
                        {/* Payment status badges — shown in all columns */}
                        <div style={{ display: "flex", gap: ".35rem", marginTop: ".35rem", flexWrap: "wrap" }}>
                          {b.DepositCents > 0 && b.DepositPaid && <span style={{ fontSize: ".7rem", color: "#10b981" }}>✓ Deposit</span>}
                          {b.PriceCents > 0 && b.BalancePaid && <span style={{ fontSize: ".7rem", color: "#10b981" }}>✓ Paid</span>}
                        </div>
                        {/* Expanded detail panel */}
                        {isExpanded && (
                          <div style={{ marginTop: "0.5rem", borderTop: "1px solid var(--border)", paddingTop: "0.5rem" }}>
                            {lineItemsLoading && <p style={{ color: "var(--muted)", fontSize: "0.8rem", margin: 0 }}>{t("bookings.loading_items")}</p>}
                            {cachedItems && cachedItems.length > 0 && (
                              <div style={{ fontSize: "0.8rem", color: "var(--text)" }}>
                                {cachedItems.map((li, idx) => (
                                  <div key={idx} style={{ display: "flex", justifyContent: "space-between", padding: "0.15rem 0" }}>
                                    <span>{li.Quantity > 1 ? `${li.Quantity}× ` : ""}{li.Name}</span>
                                    <span>{formatPrice(li.PriceCents * li.Quantity, li.Currency)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {cachedItems && cachedItems.length === 0 && (
                              <p style={{ color: "var(--muted)", fontSize: "0.8rem", margin: 0 }}>{t("bookings.no_items")}</p>
                            )}
                            <button
                              className="btn-ghost"
                              style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem", marginTop: "0.35rem" }}
                              onClick={() => handleOpenLineItems(b)}
                            >
                              Manage items
                            </button>
                          </div>
                        )}
                        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.6rem" }}>
                          <button
                            className="btn-ghost"
                            style={{ padding: "0.25rem 0.6rem", fontSize: "0.8rem" }}
                            onClick={() => setEditBooking(b)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-ghost"
                            style={{ padding: "0.25rem 0.6rem", fontSize: "0.8rem" }}
                            onClick={() => void handleExpandBooking(b.ID)}
                          >
                            {isExpanded ? "Hide" : (cachedItems && cachedItems.length > 0 ? `📦 ${t("bookings.line_items")}` : t("bookings.add_packages"))}
                          </button>
                          {b.Status === "inquiry" && (
                            <>
                              <button
                                className="btn-primary"
                                style={{ padding: "0.25rem 0.6rem", fontSize: "0.8rem" }}
                                onClick={() => handleTransition(b, "booked")}
                              >
                                {t("bookings.action.confirm")}
                              </button>
                              <button
                                className="btn-ghost"
                                style={{ padding: "0.25rem 0.6rem", fontSize: "0.8rem" }}
                                onClick={() => {
                                  if (window.confirm(`Cancel "${b.Title}"? This cannot be undone.`)) {
                                    void handleTransition(b, "cancelled");
                                  }
                                }}
                              >
                                {t("bookings.action.cancel_booking")}
                              </button>
                            </>
                          )}
                          {b.Status === "booked" && (
                            <>
                              <button
                                className="btn-primary"
                                style={{ padding: "0.25rem 0.6rem", fontSize: "0.8rem" }}
                                onClick={() => { void prefetchLineItems(b.ID); setShootDoneBooking(b); }}
                              >
                                {t("bookings.action.shoot_done")}
                              </button>
                              <button
                                className="btn-ghost"
                                style={{ padding: "0.25rem 0.6rem", fontSize: "0.8rem" }}
                                onClick={() => {
                                  if (window.confirm(`Cancel "${b.Title}"? This cannot be undone.`)) {
                                    void handleTransition(b, "cancelled");
                                  }
                                }}
                              >
                                {t("bookings.action.cancel_booking")}
                              </button>
                            </>
                          )}
                          {b.Status === "processing" && (
                            <>
                              {b.DepositCents > 0 && (
                                <div style={{ fontSize: ".75rem", color: "var(--muted)", marginTop: ".25rem", width: "100%" }}>
                                  {b.DepositPaid
                                    ? <span style={{ color: "#10b981" }}>{t("bookings.action.deposit_paid")}</span>
                                    : <button className="btn-ghost" style={{ fontSize: ".7rem", padding: ".15rem .5rem" }}
                                              onClick={() => void handleMarkDepositPaid(b.ID)}>
                                        {t("bookings.action.mark_deposit_paid")}
                                      </button>
                                  }
                                  {b.BalancePaid && <span style={{ color: "#10b981", marginLeft: ".5rem" }}>{t("bookings.action.balance_paid")}</span>}
                                </div>
                              )}
                              {b.PriceCents > 0 && !b.BalancePaid && (
                                <button
                                  className="btn-ghost"
                                  style={{ padding: "0.25rem 0.6rem", fontSize: "0.8rem", color: "#10b981" }}
                                  onClick={() => handleMarkPaid(b.ID)}
                                >
                                  {t("bookings.action.mark_paid")}
                                </button>
                              )}
                              <button
                                className="btn-primary"
                                style={{ padding: "0.25rem 0.6rem", fontSize: "0.8rem" }}
                                onClick={() => handleTransition(b, "completed")}
                              >
                                {t("bookings.action.deliver")}
                              </button>
                              <button
                                className="btn-ghost"
                                style={{ padding: "0.25rem 0.6rem", fontSize: "0.8rem" }}
                                onClick={() => {
                                  if (window.confirm(`Cancel "${b.Title}"? This cannot be undone.`)) {
                                    void handleTransition(b, "cancelled");
                                  }
                                }}
                              >
                                {t("bookings.action.cancel_booking")}
                              </button>
                            </>
                          )}
                          {(b.Status === "completed" || b.Status === "cancelled") && !b.ArchivedAt && (
                            <button
                              className="btn-ghost"
                              style={{ padding: "0.2rem 0.35rem", color: "var(--muted)", lineHeight: 0 }}
                              onClick={() => void handleArchive(b.ID)}
                              title="Archive"
                              aria-label="Archive booking"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="21 8 21 21 3 21 3 8" />
                                <rect x="1" y="3" width="22" height="5" />
                                <line x1="10" y1="12" x2="14" y2="12" />
                              </svg>
                            </button>
                          )}
                        </div>
                        {/* Activity feed — shown when expanded */}
                        {isExpanded && (
                          <div style={{ marginTop: "0.75rem", borderTop: "1px solid var(--border)", paddingTop: "0.6rem" }}>
                            <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "0.4rem" }}>{t("bookings.activity_feed")}</div>
                            {loadingActivities[b.ID] ? (
                              <p style={{ fontSize: "0.8rem", color: "var(--muted)", margin: 0 }}>{t("bookings.loading_items")}</p>
                            ) : (activities[b.ID] ?? []).length === 0 ? (
                              <p style={{ fontSize: "0.8rem", color: "var(--muted)", margin: "0 0 0.5rem" }}>{t("bookings.activity_empty")}</p>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", marginBottom: "0.5rem", maxHeight: 180, overflowY: "auto" }}>
                                {(activities[b.ID] ?? []).map(a => (
                                  <div key={a.ID} style={{ fontSize: "0.78rem" }}>
                                    <span style={{ color: a.Kind === "status_change" ? "var(--accent)" : a.Kind === "nudge" ? "#f59e0b" : "var(--text)" }}>{a.Body}</span>
                                    <span style={{ color: "var(--muted)", marginLeft: "0.4rem" }}>{new Date(a.CreatedAt).toLocaleDateString("nl-NL", { dateStyle: "short" })}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div style={{ display: "flex", gap: "0.35rem" }}>
                              <input
                                placeholder={t("bookings.note_placeholder")}
                                value={activityNote[b.ID] ?? ""}
                                onChange={e => setActivityNote(prev => ({ ...prev, [b.ID]: e.target.value }))}
                                onKeyDown={e => { if (e.key === "Enter") void submitNote(b.ID); }}
                                style={{ flex: 1, fontSize: "0.8rem", padding: "0.25rem 0.5rem" }}
                              />
                              <button className="btn-ghost" style={{ fontSize: "0.78rem", padding: "0.25rem 0.5rem" }} onClick={() => void submitNote(b.ID)}>{t("bookings.note_save")}</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                {overflow > 0 && (
                  <div className="kanban-overflow-desktop" style={{ fontSize: "0.8rem", color: "var(--muted)", padding: "0.25rem 0.1rem", textAlign: "center" }}>
                    +{overflow} older — <button className="btn-ghost" style={{ fontSize: "0.8rem", padding: "0.1rem 0.4rem" }} onClick={() => setShowArchive(true)}>view all</button>
                  </div>
                )}
                {mobileHidden > 0 && (
                  <div className="kanban-showmore-mobile" style={{ display: "none" }}>
                    <button
                      className="btn-ghost"
                      style={{ width: "100%", fontSize: "0.8rem", padding: "0.35rem 0.5rem", textAlign: "center" }}
                      onClick={() => setMobileShowAll(prev => ({ ...prev, [status]: true }))}
                    >
                      Toon meer ({mobileHidden})
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <p style={{ marginTop: "1.5rem", fontSize: "0.8rem", color: "var(--muted)" }}>
        {t("bookings.archive_settings", { archive_days: bookingSettings.booking_archive_days, nudge_days: bookingSettings.nudge_stale_days })}{" "}
        <a href="/app/account" style={{ color: "var(--accent)" }}>{t("bookings.archive_link")}</a>
      </p>
    </div>
  );
}
